export interface CurrentUser {
    email: string
}

export interface RiskBreakdownEntry {
    type: string
    weight: number
    maxConfidence: number
    coverage?: number | null
    contribution: number
}

export interface ModerationConfigData {
    healEverything: boolean
    patchThreshold: number
    reviewThreshold: number
    rejectThreshold: number
    updatedAt: string
}

export interface Job {
    id: string
    status: string
    riskScore: number
    patched: boolean
    videoUrl: string
    safeVideoUrl?: string | null
    currentStage?: string | null
    riskBreakdown?: RiskBreakdownEntry[]
    createdAt: string
    completedAt?: string | null
}

export interface Finding {
    id: string
    type: string
    confidence: number
    timestamp: string
    bbox?: { x: number; y: number; width: number; height: number } | null
    action: string
    frameIndex?: number | null
}

export interface ApiTokenSummary {
    id: string
    label?: string
    revoked: boolean
    createdAt: string
    revokedAt?: string | null
    lastUsedAt?: string | null
}

class ApiError extends Error {
    status: number
    constructor(status: number, message: string) {
        super(message)
        this.status = status
    }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await fetch(path, {
        ...options,
        credentials: "include",
        headers: { "Content-Type": "application/json", ...options.headers },
    })
    if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`)
    }
    if (res.status === 204) return undefined as T
    return res.json()
}

export const api = {
    me: () => request<CurrentUser>("/auth/me"),
    logout: () => request<void>("/auth/logout", { method: "POST" }),

    listJobs: () => request<Job[]>("/jobs"),
    getJob: (id: string) => request<Job>(`/jobs/${id}`),
    getFindings: (id: string) => request<Finding[]>(`/jobs/${id}/findings`),
    createJob: (videoUrl: string) => request<{ id: string; status: string }>("/jobs", {
        method: "POST",
        body: JSON.stringify({ videoUrl }),
    }),

    listTokens: () => request<ApiTokenSummary[]>("/admin/tokens"),
    createToken: (label: string) =>
        request<{ id: string; token: string; label?: string }>("/admin/tokens", {
            method: "POST",
            body: JSON.stringify({ label: label || undefined }),
        }),
    revokeToken: (id: string) => request<{ id: string; revoked: boolean }>(`/admin/tokens/${id}/revoke`, {
        method: "POST",
    }),

    getConfig: () => request<ModerationConfigData>("/admin/config"),
    updateConfig: (updates: Partial<Omit<ModerationConfigData, "updatedAt">>) =>
        request<ModerationConfigData>("/admin/config", { method: "PUT", body: JSON.stringify(updates) }),
}

export { ApiError }
